import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../utils/api';
import { formatRelativeTime } from '../utils/dateUtils';
import { setLastSeenTimestamp, hasUnreadMessages } from '../utils/leagueChatStorage';

interface ChatUser {
  id: string;
  teamName: string;
  kitStyle?: string;
  kitPrimaryColour?: string;
  kitSecondaryColour?: string;
}

interface MonthlyWinner {
  position: number;
  userId: string;
  teamName: string;
  totalPoints: number;
}

interface ChatMessage {
  id: string;
  type?: 'user' | 'monthly_winners';
  content: string;
  createdAt: string;
  // null for system messages such as the monthly winners announcement
  user: ChatUser | null;
  mentionedUser?: { id: string; teamName: string } | null;
  monthlyWinners?: {
    year: number;
    month: number;
    label: string;
    winners: MonthlyWinner[];
  } | null;
}

const PODIUM_MEDALS: Record<number, string> = { 1: '🏆', 2: '🥈', 3: '🥉' };
const PODIUM_PLACES: Record<number, string> = { 1: '1st', 2: '2nd', 3: '3rd' };

interface LeagueChatProps {
  leagueId: string;
  members: { id: string; teamName: string }[];
  canPost: boolean;
  canDeleteMessages: boolean;
  isVisible: boolean;
  currentUserId?: string;
  onLatestMessageChange?: (latestAt: string | null) => void;
}

function renderMessageContent(content: string, members: { id: string; teamName: string }[]) {
  if (!content.includes('@')) {
    return <span>{content}</span>;
  }

  const sortedMembers = [...members].sort((a, b) => b.teamName.length - a.teamName.length);
  const parts: React.ReactNode[] = [];
  let remaining = content;
  let key = 0;

  while (remaining.length > 0) {
    const atIndex = remaining.indexOf('@');
    if (atIndex === -1) {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }

    if (atIndex > 0) {
      parts.push(<span key={key++}>{remaining.slice(0, atIndex)}</span>);
    }

    const afterAt = remaining.slice(atIndex + 1);
    let matched = false;

    for (const member of sortedMembers) {
      if (afterAt.toLowerCase().startsWith(member.teamName.toLowerCase())) {
        const mentionText = `@${member.teamName}`;
        parts.push(
          <Link
            key={key++}
            to={`/users/${member.id}`}
            className="font-semibold hover:underline"
            style={{ color: 'var(--highlight)' }}
          >
            {mentionText}
          </Link>
        );
        remaining = afterAt.slice(member.teamName.length);
        matched = true;
        break;
      }
    }

    if (!matched) {
      parts.push(<span key={key++}>@</span>);
      remaining = afterAt;
    }
  }

  return <>{parts}</>;
}

const LeagueChat: React.FC<LeagueChatProps> = ({
  leagueId,
  members,
  canPost,
  canDeleteMessages,
  isVisible,
  currentUserId,
  onLatestMessageChange,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [error, setError] = useState('');
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionSuggestions, setMentionSuggestions] = useState<string[]>([]);
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const chatIsVisible = isVisible || isDesktop;

  const fetchMessages = useCallback(async (before?: string) => {
    const params: Record<string, string> = { limit: '10' };
    if (before) params.before = before;

    const response = await apiClient.get(`/leagues/${leagueId}/messages`, { params });
    return response.data as { messages: ChatMessage[]; hasMore: boolean; total?: number };
  }, [leagueId]);

  const loadInitialMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchMessages();
      setMessages(data.messages);
      setHasMore(data.hasMore);
      if (data.total !== undefined) setTotalCount(data.total);
      if (data.messages.length > 0) {
        onLatestMessageChange?.(data.messages[0].createdAt);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load chat');
    } finally {
      setLoading(false);
    }
  }, [fetchMessages, onLatestMessageChange]);

  useEffect(() => {
    loadInitialMessages();
  }, [loadInitialMessages]);

  useEffect(() => {
    if (messages.length === 0) {
      setHasUnread(false);
      return;
    }

    const latestAt = messages[0].createdAt;

    if (chatIsVisible) {
      setLastSeenTimestamp(leagueId, latestAt);
      setHasUnread(false);
    } else {
      setHasUnread(hasUnreadMessages(leagueId, latestAt));
    }

    onLatestMessageChange?.(latestAt);
  }, [chatIsVisible, messages, leagueId, onLatestMessageChange]);

  useEffect(() => {
    if (!chatIsVisible) return;

    const interval = setInterval(async () => {
      try {
        const data = await fetchMessages();
        if (data.total !== undefined) setTotalCount(data.total);
        if (data.messages.length > 0) {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newOnes = data.messages.filter(m => !existingIds.has(m.id));
            if (newOnes.length === 0) return prev;
            return [...newOnes, ...prev].sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
          });
          onLatestMessageChange?.(data.messages[0].createdAt);
        }
      } catch {
        // Silently ignore polling errors
      }
    }, 240000);

    return () => clearInterval(interval);
  }, [chatIsVisible, fetchMessages, onLatestMessageChange]);

  const loadMoreMessages = useCallback(async () => {
    if (loadingMore || !hasMore || messages.length === 0) return;

    const oldest = messages[messages.length - 1];
    try {
      setLoadingMore(true);
      const data = await fetchMessages(oldest.createdAt);
      setMessages(prev => [...prev, ...data.messages]);
      setHasMore(data.hasMore);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load more messages');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, messages, fetchMessages]);

  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    const container = messagesContainerRef.current;
    if (!trigger || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreMessages();
        }
      },
      { root: container, threshold: 0.1 }
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [loadMoreMessages]);

  const handleInputChange = (value: string) => {
    setNewMessage(value);

    const cursorPos = inputRef.current?.selectionStart ?? value.length;
    const textBeforeCursor = value.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@([\w\s-]*)$/);

    if (atMatch) {
      const query = atMatch[1].toLowerCase();
      setMentionQuery(query);
      setMentionSuggestions(
        members
          .map(m => m.teamName)
          .filter(name => name.toLowerCase().includes(query))
          .slice(0, 5)
      );
      setMentionHighlightIndex(0);
    } else {
      setMentionQuery(null);
      setMentionSuggestions([]);
      setMentionHighlightIndex(0);
    }
  };

  const insertMention = (teamName: string) => {
    const cursorPos = inputRef.current?.selectionStart ?? newMessage.length;
    const textBeforeCursor = newMessage.slice(0, cursorPos);
    const textAfterCursor = newMessage.slice(cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const updated = textBeforeCursor.slice(0, atIndex) + `@${teamName} ` + textAfterCursor;
    setNewMessage(updated);
    setMentionQuery(null);
    setMentionSuggestions([]);
    setMentionHighlightIndex(0);
    inputRef.current?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newMessage.trim();
    if (!trimmed || sending) return;

    try {
      setSending(true);
      setError('');
      const response = await apiClient.post(`/leagues/${leagueId}/messages`, { content: trimmed });
      const message = response.data as ChatMessage;
      setMessages(prev => [message, ...prev]);
      setTotalCount(prev => (prev ?? 0) + 1);
      setNewMessage('');
      setMentionQuery(null);
      setMentionSuggestions([]);
      onLatestMessageChange?.(message.createdAt);
      if (chatIsVisible) {
        setLastSeenTimestamp(leagueId, message.createdAt);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (messageId: string) => {
    if (deletingId) return;

    try {
      setDeletingId(messageId);
      setError('');
      await apiClient.delete(`/leagues/${leagueId}/messages/${messageId}`);
      setMessages(prev => {
        const updated = prev.filter(m => m.id !== messageId);
        onLatestMessageChange?.(updated.length > 0 ? updated[0].createdAt : null);
        return updated;
      });
      setTotalCount(prev => Math.max(0, (prev ?? 1) - 1));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete message');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="card rounded-lg shadow bg-gray-50 dark:bg-gray-800/30 flex flex-col h-full min-h-[400px] md:min-h-[500px] md:max-h-[700px]">
      <div className="px-6 py-4 border-b flex-shrink-0 flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
        <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>League Chat</h2>
        {!loading && totalCount !== null && (
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              hasUnread ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400' : ''
            }`}
            style={
              hasUnread
                ? undefined
                : { backgroundColor: 'rgba(156, 163, 175, 0.2)', color: 'var(--text-secondary)' }
            }
          >
            {totalCount}
          </span>
        )}
      </div>

      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0"
      >
        {loading ? (
          <div className="text-center py-8 text-sm" style={{ color: 'var(--text-secondary)' }}>
            Loading chat...
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-sm" style={{ color: 'var(--text-secondary)' }}>
            No messages yet. Start the conversation!
          </div>
        ) : (
          <>
            {messages.map(message => message.monthlyWinners ? (
              <div key={message.id} className="group text-sm chat-winners-tile px-3 py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-bold">
                    {message.monthlyWinners.label} winners
                  </span>
                  {canDeleteMessages && (
                    <button
                      type="button"
                      onClick={() => handleDelete(message.id)}
                      disabled={deletingId === message.id}
                      className="text-xs text-red-600 hover:text-red-800 hover:underline opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 flex-shrink-0"
                    >
                      {deletingId === message.id ? 'Deleting...' : 'Delete'}
                    </button>
                  )}
                </div>
                <ul className="mt-2 space-y-1">
                  {message.monthlyWinners.winners.map(winner => (
                    <li key={winner.userId} className="flex items-center gap-2">
                      <span aria-hidden="true">{PODIUM_MEDALS[winner.position]}</span>
                      <span className="sr-only">{PODIUM_PLACES[winner.position]}</span>
                      <Link
                        to={`/users/${winner.userId}`}
                        className="font-semibold hover:underline min-w-0 truncate"
                      >
                        {winner.teamName}
                      </Link>
                      {winner.userId === currentUserId && (
                        <span className="px-2 py-0.5 text-xs bg-highlight text-black rounded-full">You</span>
                      )}
                      <span className="ml-auto flex-shrink-0 text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {winner.totalPoints} {winner.totalPoints === 1 ? 'pt' : 'pts'}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Congrats to this month's top {message.monthlyWinners.winners.length === 1 ? 'predictor' : 'predictors'}!
                </p>
              </div>
            ) : (
              <div key={message.id} className="group text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  {message.user ? (
                    <Link
                      to={`/users/${message.user.id}`}
                      className="font-semibold hover:underline min-w-0"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {message.user.teamName}
                    </Link>
                  ) : (
                    <span className="font-semibold min-w-0" style={{ color: 'var(--text-secondary)' }}>
                      PredictorPro
                    </span>
                  )}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {formatRelativeTime(message.createdAt)}
                    </span>
                    {canDeleteMessages && (
                      <button
                        type="button"
                        onClick={() => handleDelete(message.id)}
                        disabled={deletingId === message.id}
                        className="text-xs text-red-600 hover:text-red-800 hover:underline opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                      >
                        {deletingId === message.id ? 'Deleting...' : 'Delete'}
                      </button>
                    )}
                  </div>
                </div>
                <p className="mt-0.5 break-words" style={{ color: 'var(--text-primary)' }}>
                  {renderMessageContent(message.content, members)}
                </p>
              </div>
            ))}

            {hasMore && (
              <div ref={loadMoreTriggerRef} className="text-center py-2">
                {loadingMore ? (
                  <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Loading more...</span>
                ) : (
                  <button
                    onClick={loadMoreMessages}
                    className="text-xs hover:underline"
                    style={{ color: 'var(--highlight)' }}
                  >
                    Load older messages
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {canPost && (
        <div className="px-4 py-3 border-t flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          {error && (
            <p className="text-xs text-red-600 mb-2">{error}</p>
          )}
          <form onSubmit={handleSubmit} className="relative">
            {mentionSuggestions.length > 0 && (
              <div
                className="absolute bottom-full left-0 right-0 mb-1 rounded border shadow-lg z-10 max-h-32 overflow-y-auto"
                style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)' }}
              >
                {mentionSuggestions.map((name, index) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => insertMention(name)}
                    className={`w-full text-left px-3 py-2 text-sm ${
                      index === mentionHighlightIndex
                        ? 'bg-gray-100 dark:bg-gray-700'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    style={{ color: 'var(--text-primary)' }}
                  >
                    @{name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (mentionSuggestions.length > 0) {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      insertMention(mentionSuggestions[mentionHighlightIndex]);
                      return;
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setMentionQuery(null);
                      setMentionSuggestions([]);
                      setMentionHighlightIndex(0);
                      return;
                    }
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setMentionHighlightIndex(i => Math.min(i + 1, mentionSuggestions.length - 1));
                      return;
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setMentionHighlightIndex(i => Math.max(i - 1, 0));
                      return;
                    }
                  }
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder="Type a message..."
                rows={1}
                maxLength={500}
                className="flex-1 px-3 py-2 rounded border text-sm resize-none"
                style={{
                  backgroundColor: 'var(--surface)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)',
                }}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="btn-primary px-3 py-2 rounded text-sm self-end disabled:opacity-50"
              >
                {sending ? '...' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default LeagueChat;
